import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBrand } from '@/shared/brand/useBrand';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { LocaleSwitcher } from '@/shared/i18n/LocaleSwitcher';
import { useI18n } from '@/shared/i18n/useT';
import type { MessageKey } from '@/shared/i18n/messages';

export type LegalDocument = 'privacy' | 'terms';

interface Props {
  readonly document: LegalDocument;
}

// Noon UTC keeps the displayed calendar date stable in the supported locales.
const LEGAL_DATE = new Date('2026-09-06T12:00:00Z');

const SECTIONS: Readonly<Record<LegalDocument, readonly string[]>> = {
  privacy: ['responsible', 'collection', 'use', 'consent', 'sensitive', 'google', 'sharing', 'processors', 'storage', 'retention', 'security', 'rights', 'authority'],
  terms: ['acceptance', 'role', 'accounts', 'age', 'organizations', 'processing', 'content', 'bookings', 'use', 'reports', 'plans', 'scope', 'withdrawal', 'availability', 'indemnity', 'liability', 'termination', 'law', 'changes'],
};

/**
 * Res. 104/2005 art. 3 lists the provider's name, domicile, electronic address,
 * tax identification and a customer-service phone. We publish the electronic
 * addresses — derived from the brand host, which is why they are not hardcoded —
 * and nothing that identifies the operator personally.
 *
 * That is a deliberate, recorded decision, not an oversight. The operator is an
 * individual working alone from home; publishing a CUIT beside a home locality
 * is an impersonation and fraud vector in Argentina (ARCA itself gates the
 * equivalent lookup behind a captcha), and a phone link is the only channel
 * letting a stranger reach him instantly at any hour. The role mailboxes carry
 * the contact duty, the Terms constitute them as the domicile for notices, and
 * full identification is supplied on request to anyone with a legitimate need.
 * See docs/LEGAL.md for the reasoning and the residual exposure.
 */
const ROLE_MAILBOXES = ['legal', 'privacy', 'support'] as const;
type RoleMailbox = (typeof ROLE_MAILBOXES)[number];

const MAILBOX_LABEL: Readonly<Record<RoleMailbox, MessageKey>> = {
  legal: 'legal.contactLegalLabel',
  privacy: 'legal.contactPrivacyLabel',
  support: 'legal.contactSupportLabel',
};

export function LegalPage({ document: documentKind }: Props) {
  const { formatDate, t } = useI18n();
  const brand = useBrand();
  const title = t(`legal.${documentKind}.title` as 'legal.privacy.title');
  const description = t(`legal.${documentKind}.description` as 'legal.privacy.description');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} · ${brand.name}`;

    const descriptionMeta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = descriptionMeta?.content;
    if (descriptionMeta) descriptionMeta.content = description;

    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = canonical?.href;
    if (canonical) {
      const canonicalUrl = new URL(window.location.href);
      canonicalUrl.search = '';
      canonicalUrl.hash = '';
      canonical.href = canonicalUrl.toString();
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta && previousDescription !== undefined) descriptionMeta.content = previousDescription;
      if (canonical && previousCanonical !== undefined) canonical.href = previousCanonical;
    };
  }, [brand.name, description, title]);

  const homePath = brand.id === 'numtable' ? '/signin' : '/book';
  const sectionPrefix = `legal.${documentKind}.sections`;

  return (
    <div className="-mx-3 min-h-[calc(100dvh-3rem)] bg-surface sm:-mx-6">
      <header className="border-b border-border bg-surface-2">
        <div className="mx-auto flex min-h-header max-w-4xl items-center justify-between gap-4 px-3 sm:px-6">
          <Link to={homePath} className="tap-target flex items-center rounded-token" aria-label={brand.name}>
            <BrandLogo className="h-6 w-auto sm:h-7" />
          </Link>
          <div className="flex items-center gap-2">
            <LocaleSwitcher compact />
            <nav aria-label={t('legal.navigation')} className="hidden items-center gap-1 text-sm sm:flex">
              <Link to="/privacy" className={documentKind === 'privacy' ? activeLegalLink : legalLink}>
                {t('legal.privacy.shortTitle')}
              </Link>
              <Link to="/terms" className={documentKind === 'terms' ? activeLegalLink : legalLink}>
                {t('legal.terms.shortTitle')}
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-3 py-10 sm:px-6 sm:py-14">
        <article className="max-w-3xl">
          <p className="text-sm font-medium text-accent">{t('legal.productName')}</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-fg sm:text-4xl">{title}</h1>
          <p className="mt-4 text-base leading-7 text-muted">{t(`legal.${documentKind}.intro` as 'legal.privacy.intro')}</p>
          <p className="mt-3 text-sm text-muted">
            <span>{t('legal.lastUpdated')}: </span>
            <time dateTime="2026-09-06">{formatDate(LEGAL_DATE, { dateStyle: 'long' })}</time>
          </p>

          <div className="mt-10 space-y-8">
            {SECTIONS[documentKind].map((section) => (
              <section key={section}>
                <h2 className="text-xl font-semibold text-fg">
                  {t(`${sectionPrefix}.${section}.title` as 'legal.privacy.sections.collection.title')}
                </h2>
                <p className="mt-3 whitespace-pre-line text-base leading-7 text-muted">
                  {t(`${sectionPrefix}.${section}.body` as 'legal.privacy.sections.collection.body')}
                </p>
              </section>
            ))}
          </div>

          <div className="mt-10 rounded-token border border-border bg-surface-2 p-5 sm:p-6">
            <h2 className="text-xl font-semibold text-fg">{t('legal.entity.title')}</h2>
            <p className="mt-3 text-base leading-7 text-muted">{t('legal.entity.intro')}</p>
            <p className="mt-4 text-sm leading-6 text-muted">{t('legal.entity.addressNote')}</p>
          </div>

          <div className="mt-6 rounded-token border border-border bg-surface-2 p-5 sm:p-6">
            <h2 className="text-xl font-semibold text-fg">{t('legal.contactTitle')}</h2>
            <p className="mt-3 text-base leading-7 text-muted">{t('legal.contactBody')}</p>
            <ul className="mt-4 space-y-3">
              {ROLE_MAILBOXES.map((mailbox) => (
                <li key={mailbox}>
                  <p className="text-sm font-medium text-fg">{t(MAILBOX_LABEL[mailbox])}</p>
                  <a
                    href={`mailto:${mailbox}@${brand.host}`}
                    className="inline-flex min-h-11 items-center rounded-token text-sm font-medium text-accent underline decoration-accent/40 underline-offset-4 hover:text-accent-hover"
                  >
                    {`${mailbox}@${brand.host}`}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </article>
      </div>

      <footer className="border-t border-border bg-surface-2">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-x-4 gap-y-2 px-3 py-5 text-sm text-muted sm:px-6">
          <span>{t('legal.footer')}</span>
          <Link to="/privacy" className="text-accent hover:text-accent-hover">{t('legal.privacy.shortTitle')}</Link>
          <Link to="/terms" className="text-accent hover:text-accent-hover">{t('legal.terms.shortTitle')}</Link>
        </div>
      </footer>
    </div>
  );
}

const legalLink = 'tap-target inline-flex items-center rounded-token px-3 py-2 text-muted hover:bg-surface-3 hover:text-fg';
const activeLegalLink = 'tap-target inline-flex items-center rounded-token bg-accent-soft px-3 py-2 font-medium text-accent';
